/**
 * features/communications/NoticeReadTracking.tsx
 * ------------------------------------------------------------------
 * Staff-side modal: for one broadcast notice, show how many of the
 * resolved recipients have opened it ("seen by X of Y"), with a
 * per-parent breakdown.
 *
 * Y = notice.recipientParentUids (the authoritative delivery list).
 * X = recipients who have a `reads/{uid}` receipt. The reads
 * subcollection is subscribed live, so the count updates as parents
 * open the notice. Recipient names resolve from the loaded `users`
 * directory (this modal is opened from the admin/head composer, which
 * has that directory); the read receipt's own `parentName` is used as
 * a fallback.
 * ------------------------------------------------------------------
 */
import { useEffect, useMemo, useState } from 'react';
import { collection, onSnapshot } from 'firebase/firestore';
import { Users, CheckCircle2, Circle, Search } from 'lucide-react';
import { db } from '@/lib/firebase';
import { useData } from '@/contexts/DataContext';
import { FormModal } from '@/components/ui/glass';
import {
  PARENT_NOTICES_COLLECTION,
  PARENT_NOTICE_READS_SUBCOLLECTION,
  type ParentNotice,
} from '@/lib/parentNotices';

interface ReadReceipt {
  uid: string;
  readAt?: string;
  parentName?: string;
}

export default function NoticeReadTracking({
  notice,
  onClose,
}: {
  notice: ParentNotice;
  onClose: () => void;
}) {
  const { users } = useData();
  const [reads, setReads] = useState<Record<string, ReadReceipt>>({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    const unsub = onSnapshot(
      collection(
        db,
        PARENT_NOTICES_COLLECTION,
        notice.id,
        PARENT_NOTICE_READS_SUBCOLLECTION,
      ),
      (snap) => {
        const map: Record<string, ReadReceipt> = {};
        snap.docs.forEach((d) => {
          const data = d.data() as { readAt?: string; parentName?: string };
          map[d.id] = { uid: d.id, readAt: data.readAt, parentName: data.parentName };
        });
        setReads(map);
        setLoading(false);
      },
      (err) => {
        console.warn('[notice-reads] subscription error:', err);
        setLoading(false);
      },
    );
    return unsub;
  }, [notice.id]);

  /** uid -> display name from the loaded directory. */
  const nameByUid = useMemo(() => {
    const m = new Map<string, string>();
    users.forEach((u) => {
      const uid = u.uid ?? u.id; // gotcha #1
      if (uid) m.set(uid, u.displayName);
    });
    return m;
  }, [users]);

  const recipients = notice.recipientParentUids ?? [];

  const rows = useMemo(() => {
    const list = recipients.map((uid) => {
      const r = reads[uid];
      return {
        uid,
        name: nameByUid.get(uid) ?? r?.parentName ?? 'Unknown parent',
        readAt: r?.readAt,
        read: !!r,
      };
    });
    // Read first (most recent open first), then unread alphabetically.
    list.sort((a, b) => {
      if (a.read !== b.read) return a.read ? -1 : 1;
      if (a.read && b.read) return (b.readAt ?? '').localeCompare(a.readAt ?? '');
      return a.name.localeCompare(b.name);
    });
    const q = search.trim().toLowerCase();
    return q ? list.filter((r) => r.name.toLowerCase().includes(q)) : list;
  }, [recipients, reads, nameByUid, search]);

  const seen = recipients.filter((uid) => reads[uid]).length;
  const total = recipients.length;
  const pct = total > 0 ? Math.round((seen / total) * 100) : 0;

  return (
    <FormModal
      icon={Users}
      title="Read receipts"
      subtitle={notice.title}
      onClose={onClose}
      maxWidth="max-w-lg"
    >
      {/* Summary */}
      <div className="mb-4">
        <div className="flex items-baseline justify-between mb-2">
          <span className="text-sm font-semibold text-gray-900 dark:text-white">
            Seen by {seen} of {total}
          </span>
          <span className="text-xs text-gray-400">{pct}%</span>
        </div>
        <div className="h-2 rounded-full bg-gray-100 dark:bg-gray-700 overflow-hidden">
          <div
            className="h-full rounded-full bg-[#2d5295] transition-all"
            style={{ width: `${pct}%` }}
          />
        </div>
      </div>

      {/* Search (helpful for large broadcasts) */}
      {total > 8 && (
        <div className="relative mb-3">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            className="w-full pl-9 pr-3 py-2 text-sm border-2 rounded-xl bg-white border-gray-200 focus:border-[#2d5295] focus:outline-none dark:bg-gray-800 dark:border-gray-600 dark:text-white"
            placeholder="Search parents…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      )}

      {/* List */}
      {loading ? (
        <p className="text-sm text-gray-400 text-center py-6">Loading receipts…</p>
      ) : total === 0 ? (
        <p className="text-sm text-gray-400 text-center py-6">
          This notice has no resolved recipients.
        </p>
      ) : (
        <div className="max-h-72 overflow-y-auto rounded-xl border border-gray-100 dark:border-gray-700 divide-y divide-gray-50 dark:divide-gray-800">
          {rows.map((r) => (
            <div key={r.uid} className="flex items-center gap-3 px-3 py-2.5 text-sm">
              {r.read ? (
                <CheckCircle2 size={16} className="text-emerald-500 shrink-0" />
              ) : (
                <Circle size={16} className="text-gray-300 shrink-0" />
              )}
              <span
                className={`min-w-0 truncate ${
                  r.read
                    ? 'text-gray-800 dark:text-gray-100'
                    : 'text-gray-500 dark:text-gray-400'
                }`}
              >
                {r.name}
              </span>
              <span className="ml-auto shrink-0 text-xs text-gray-400">
                {r.read && r.readAt
                  ? new Date(r.readAt).toLocaleDateString(undefined, {
                      month: 'short',
                      day: 'numeric',
                    })
                  : 'Not yet seen'}
              </span>
            </div>
          ))}
          {rows.length === 0 && (
            <div className="px-3 py-4 text-sm text-gray-400 text-center">
              No parents match that search.
            </div>
          )}
        </div>
      )}
    </FormModal>
  );
}
